import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Activity, Target, Users, CheckCircle, Clock, Plus, Edit2, Trash2, Send, ThumbsUp, AlertCircle, X, Flag, ChevronDown, ChevronUp, ChevronRight, GripVertical, Archive, RotateCcw, LayoutGrid, Calendar, Award, Shield, CheckSquare, ExternalLink, Check, Loader2 } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import GanttChart from '../components/activities/GanttChart';
import SkillRequirementsManager from '../components/activities/SkillRequirementsManager';
import PrinciplesSection from '../components/activities/PrinciplesSection';
import ActivityPrinciplesValidation from '../components/commander/ActivityPrinciplesValidation';
import ProgressReportPrinciplesValidation from '../components/commander/ProgressReportPrinciplesValidation';
import RemovedIssuesSection from '../components/activities/RemovedIssuesSection';
import TagRemovalDialog from '../components/activities/TagRemovalDialog';
import TagHistoryDialog from '../components/activities/TagHistoryDialog';
import ArchiveWarningDialog from '@/components/common/ArchiveWarningDialog';
import ArchivedParentBadge from '@/components/common/ArchivedParentBadge';
import ActivityHeaderCell from '@/components/activities/ActivityHeaderCell';
import AutoScrollContainer from '@/components/common/AutoScrollContainer';
import AITaskApprovalSection from '../components/activities/AITaskApprovalSection';
import BotTaskGenerationSection from '../components/activities/BotTaskGenerationSection';
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { MeasurementCombobox, FormattedNumberInput, TIER_FILTERS } from "@/components/bullseye/BullseyeFormComponents";
import { format } from 'date-fns';

export default function Activities() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activityDialog, setActivityDialog] = useState(false);
  const [reportDialog, setReportDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [missionFilters, setMissionFilters] = useState([]);
  const [objectiveFilters, setObjectiveFilters] = useState([]);

  const [groupByMode, setGroupByMode] = useState('accountable'); // 'accountable' or 'responsible'
  const [expandedOfficers, setExpandedOfficers] = useState({}); // track which officer rows are expanded
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'gantt'

  const [activityForm, setActivityForm] = useState({
    mission_id: '',
    objective_id: '',
    title: '',
    description: '',
    scope: '',
    responsible_email: '',
    consulted_emails: [],
    informed_emails: [],
    eisenhower_quadrant: 'not_urgent_important',
    key_results: [],
    due_date: '',
    skill_requirements: [],
    skill_rewards: [],
    principles_adhered: [],
    red_line_considerations: [],
    narrative_on_principles: ''
  });

  const [reportForm, setReportForm] = useState({
    key_results_progress: [],
    issues_encountered: [],
    additional_context: '',
    flagged_items: [],
    narrative_on_principles: '',
    proposed_status_flag: 'yellow'
  });
  const [currentTagType, setCurrentTagType] = useState('');
  const [currentTagValue, setCurrentTagValue] = useState('');
  const [expandedReportsInGantt, setExpandedReportsInGantt] = useState({});
  const [validatingActivity, setValidatingActivity] = useState(null);
  const [validatingReport, setValidatingReport] = useState({ activity: null, reportIndex: null });
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [activityToArchive, setActivityToArchive] = useState(null);
  const [archiveDependents, setArchiveDependents] = useState([]);
  const [tagRemovalDialog, setTagRemovalDialog] = useState({ open: false, tagType: null, tagValue: null, tagLabel: null, issueId: null });
  const [tagHistoryDialog, setTagHistoryDialog] = useState({ open: false, tag: null });
  const [scopeSuggesting, setScopeSuggesting] = useState(false);
  const [scopeSuggestion, setScopeSuggestion] = useState(null);
  const [scopeValidating, setScopeValidating] = useState(false);
  const [scopeValidation, setScopeValidation] = useState(null);

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: profile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === user?.email);
    },
    enabled: !!user
  });

  const isCommander = user?.role === 'admin' || profile?.custom_role === 'Commander';

  const { data: officers } = useQuery({
    queryKey: ['officers'],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles;
    },
    initialData: []
  });

  const { data: missions } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: objectives } = useQuery({
    queryKey: ['objectives'],
    queryFn: async () => {
      const allObjectives = await base44.entities.Objective.list();
      return allObjectives;
    },
    enabled: !!user,
    initialData: []
  });

  const { data: activities } = useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  const { data: progressReports } = useQuery({
    queryKey: ['progressReports'],
    queryFn: () => base44.entities.ProgressReport.list(),
    initialData: []
  });

  const { data: reportIssues } = useQuery({
    queryKey: ['reportIssues'],
    queryFn: () => base44.entities.ReportIssue.list(),
    initialData: []
  });

  const { data: artifacts } = useQuery({
    queryKey: ['artifacts'],
    queryFn: () => base44.entities.Artifact.list(),
    initialData: []
  });

  const { data: rituals } = useQuery({
    queryKey: ['rituals'],
    queryFn: () => base44.entities.Ritual.list(),
    initialData: []
  });

  const { data: policies } = useQuery({
    queryKey: ['policies'],
    queryFn: () => base44.entities.Policy.list(),
    initialData: []
  });

  const { data: capabilities } = useQuery({
    queryKey: ['capabilities'],
    queryFn: () => base44.entities.Capability.list(),
    initialData: []
  });

  const { data: dailyTasks } = useQuery({
    queryKey: ['dailyTasks'],
    queryFn: () => base44.entities.DailyTask.list(),
    initialData: []
  });

  const { data: vocabRecords } = useQuery({
    queryKey: ['bullseyeVocabulary'],
    queryFn: () => base44.entities.BullseyeVocabulary.list(),
    initialData: []
  });

  const measurements = (vocabRecords || []).filter(v => v.type === 'measurement' && v.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const measurementCategories = [...new Set(measurements.map(m => m.category))];

  // Filter missions based on user role
  const availableMissions = missions.filter((mission) => {
    // Show all if commander
    if (isCommander) return true;

    // Show if user is owner or sponsor
    if (mission.owner_email === user?.email || mission.sponsor_email === user?.email) return true;

    // Show if user is in any activity under this mission
    const missionObjectives = objectives.filter((obj) => obj.mission_id === mission.id);
    const missionObjectiveIds = missionObjectives.map((obj) => obj.id);
    const userInActivity = activities.some((activity) =>
    missionObjectiveIds.includes(activity.objective_id) && (
    activity.accountable_email === user?.email ||
    activity.responsible_email === user?.email ||
    (activity.consulted_emails || []).includes(user?.email) ||
    (activity.informed_emails || []).includes(user?.email))
    );

    // Show if user is an affiliated officer of the mission
    const isAffiliatedOfficer = (mission.affiliated_officers || []).includes(user?.email);

    return userInActivity || isAffiliatedOfficer;
  });

  // Filter objectives based on selected mission
  const availableObjectives = activityForm.mission_id ?
  objectives.filter((obj) => obj.mission_id === activityForm.mission_id && obj.status === 'active') :
  [];

  const filteredActivities = activities.filter((activity) => {
    // Filter by status
    if (statusFilter !== 'all' && activity.status !== statusFilter) return false;

    // Filter by Missions
    if (missionFilters.length > 0) {
       const objective = objectives.find(o => o.id === activity.objective_id);
       if (!objective || !missionFilters.includes(objective.mission_id)) return false;
    }

    // Filter by Objectives
    if (objectiveFilters.length > 0) {
       if (!objectiveFilters.includes(activity.objective_id)) return false;
    }

    // Only show activities where user is accountable or responsible
    return activity.accountable_email === user?.email ||
    activity.responsible_email === user?.email ||
    (activity.consulted_emails || []).includes(user?.email) ||
    (activity.informed_emails || []).includes(user?.email);
  });

  const createActivityMutation = useMutation({
    mutationFn: async (data) => {
      // Generate activity code with unique sequential number
      const objective = objectives.find((o) => o.id === data.objective_id);
      const objectiveActivities = activities.filter((a) => a.objective_id === data.objective_id);

      // Find the maximum activity number for this objective
      const maxActivityNumber = objectiveActivities.reduce((max, activity) => {
        if (activity.activity_code) {
          const match = activity.activity_code.match(/-A(\d+)$/);
          if (match) {
            const num = parseInt(match[1], 10);
            return num > max ? num : max;
          }
        }
        return max;
      }, 0);

      const activity_code = `${objective?.objective_code || 'UNK'}-A${maxActivityNumber + 1}`;

      // Get objective owner as accountable (not mission owner)
      const accountable_email = objective?.owner_email;
      if (!accountable_email) {
        throw new Error('Selected objective does not have an owner. Cannot create activity.');
      }
      
      // Check permission: only objective owner, mission owner, or mission sponsor can create
      const mission = missions.find((m) => m.id === data.mission_id);
      const canCreate = 
        accountable_email === user?.email || 
        mission?.owner_email === user?.email || 
        mission?.sponsor_email === user?.email;
      
      if (!canCreate) {
        throw new Error('Only the objective owner, mission owner, or mission sponsor can create activities');
      }

      // Calculate initial key_results_progress
      let key_results_progress = 0;
      if (data.key_results && data.key_results.length > 0) {
        const totalProgress = data.key_results.reduce((sum, kr) => {
          const current = parseFloat(kr.current_value) || 0;
          const target = parseFloat(kr.target) || 1;
          return sum + (target > 0 ? (current / target) * 100 : 0);
        }, 0);
        key_results_progress = Math.round(totalProgress / data.key_results.length);
      }

      return base44.entities.Activity.create({
        ...data,
        activity_code,
        accountable_email,
        status: 'active',
        status_flag: 'yellow',
        key_results: data.key_results || [],
        key_results_progress
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      setActivityDialog(false);
      resetForm();
    },
    onError: (error) => {
      alert(error.message);
    }
  });

  const updateActivityMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Activity.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      setActivityDialog(false);
      setEditingActivity(null);
      resetForm();
    }
  });

  const deleteActivityMutation = useMutation({
    mutationFn: async (id) => {
      const activityTasks = dailyTasks.filter(t => t.activity_id === id);
      if (activityTasks.length > 0) {
        throw new Error('Cannot delete activity with existing tasks');
      }
      return base44.entities.Activity.delete(id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['activities'] })
  });

  // This is now only for updating the key_results_progress percentage (calculated from KR values)
  const updateKeyResultsProgressMutation = useMutation({
    mutationFn: ({ activityId, key_results }) => {
      // Calculate progress percentage
      let progress = 0;
      if (key_results && key_results.length > 0) {
        const totalProgress = key_results.reduce((sum, kr) => {
          const current = parseFloat(kr.current_value) || 0;
          const target = parseFloat(kr.target) || 1;
          return sum + (target > 0 ? (current / target) * 100 : 0);
        }, 0);
        progress = Math.round(totalProgress / key_results.length);
      }
      return base44.entities.Activity.update(activityId, {
        key_results_progress: progress
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['activities'] })
  });

  const submitReportMutation = useMutation({
    mutationFn: async ({ activityId, report }) => {
      // Create progress report with proposed_status_flag (not yet applied to activity)
      const newReport = await base44.entities.ProgressReport.create({
        activity_id: activityId,
        reported_by: user?.email,
        reported_at: new Date().toISOString(),
        key_results_progress: report.key_results_progress,
        proposed_status_flag: report.proposed_status_flag || 'yellow',
        additional_context: report.additional_context,
        flagged_items: report.flagged_items,
        narrative_on_principles: report.narrative_on_principles,
        approved: false
      });

      // Create report issues
      if (report.issues_encountered && report.issues_encountered.length > 0) {
        await Promise.all(
          report.issues_encountered.map(issue =>
            base44.entities.ReportIssue.create({
              progress_report_id: newReport.id,
              ...issue
            })
          )
        );
      }

      // Don't update activity status_flag yet - only update key results progress
      const activity = activities.find(a => a.id === activityId);
      let key_results_progress = 0;
      if (report.key_results_progress && report.key_results_progress.length > 0) {
        const totalProgress = report.key_results_progress.reduce((sum, kr) => {
          const current = parseFloat(kr.new_value) || 0;
          const target = parseFloat(activity?.key_results?.find(k => k.description === kr.description)?.target) || 1;
          return sum + (target > 0 ? (current / target) * 100 : 0);
        }, 0);
        key_results_progress = Math.round(totalProgress / report.key_results_progress.length);
      }

      return base44.entities.Activity.update(activityId, {
        key_results_progress
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['progressReports'] });
      queryClient.invalidateQueries({ queryKey: ['reportIssues'] });
      setReportDialog(false);
      setSelectedActivity(null);
      setReportForm({ key_results_progress: [], issues_encountered: [], additional_context: '', flagged_items: [], narrative_on_principles: '', proposed_status_flag: 'yellow' });
    }
  });

  const approveReportMutation = useMutation({
    mutationFn: async ({ activityId, reportId }) => {
      const activity = activities.find((a) => a.id === activityId);
      const report = progressReports.find((r) => r.id === reportId);

      // Update progress report
      await base44.entities.ProgressReport.update(reportId, {
        approved: true,
        approved_by: user?.email,
        approved_at: new Date().toISOString()
      });

      // Update key results with the latest approved values
      let updatedKeyResults = [...(activity.key_results || [])];
      if (report.key_results_progress) {
        report.key_results_progress.forEach((progress) => {
          const krIndex = updatedKeyResults.findIndex((kr) => kr.description === progress.description);
          if (krIndex !== -1) {
            updatedKeyResults[krIndex].current_value = progress.new_value;
          }
        });
      }

      // Calculate key_results_progress percentage
      let key_results_progress = 0;
      if (updatedKeyResults.length > 0) {
        const totalProgress = updatedKeyResults.reduce((sum, kr) => {
          const current = parseFloat(kr.current_value) || 0;
          const target = parseFloat(kr.target) || 1;
          return sum + (target > 0 ? (current / target) * 100 : 0);
        }, 0);
        key_results_progress = Math.round(totalProgress / updatedKeyResults.length);
      }

      // Award skill points if activity has rewards
      if (activity.skill_rewards && activity.skill_rewards.length > 0) {
        const responsibleOfficer = officers.find((o) => o.user_email === activity.responsible_email);
        if (responsibleOfficer) {
          const updatedSkills = [...(responsibleOfficer.skills || [])];

          activity.skill_rewards.forEach((reward) => {
            const skillIndex = updatedSkills.findIndex((s) => s.name === reward.skill_name);
            if (skillIndex >= 0) {
              const newXp = (updatedSkills[skillIndex].xp || 0) + reward.sp_amount;
              const SP_PER_LEVEL = [0, 100, 250, 500, 1000, 2000];
              let newLevel = 1;
              for (let i = SP_PER_LEVEL.length - 1; i >= 0; i--) {
                if (newXp >= SP_PER_LEVEL[i]) {
                  newLevel = i;
                  break;
                }
              }
              updatedSkills[skillIndex] = {
                ...updatedSkills[skillIndex],
                xp: newXp,
                level: Math.max(updatedSkills[skillIndex].level, newLevel)
              };
            } else {
              updatedSkills.push({
                name: reward.skill_name,
                level: 1,
                xp: reward.sp_amount
              });
            }
          });

          await base44.entities.OfficerProfile.update(responsibleOfficer.id, { skills: updatedSkills });
        }
      }

      // NOW apply the proposed_status_flag to the activity upon approval
      return base44.entities.Activity.update(activityId, {
        key_results: updatedKeyResults,
        key_results_progress,
        status_flag: report.proposed_status_flag || activity.status_flag,
        status_flag_updated_at: new Date().toISOString(),
        status_flag_updated_by: user?.email
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['progressReports'] });
      queryClient.invalidateQueries({ queryKey: ['officers'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    }
  });

  const reorderActivitiesMutation = useMutation({
    mutationFn: async ({ activityIds }) => {
      const updates = activityIds.map((id, index) =>
      base44.entities.Activity.update(id, { display_order: index })
      );
      await Promise.all(updates);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['activities'] })
  });

  const archiveActivityMutation = useMutation({
    mutationFn: (id) => base44.entities.Activity.update(id, { status: 'archived' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
    }
  });

  const unarchiveActivityMutation = useMutation({
    mutationFn: (id) => base44.entities.Activity.update(id, {
      status: 'active',
      reactivated_at: new Date().toISOString(),
      status_flag: 'yellow'
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['activities'] })
  });

  const resetForm = () => {
    setActivityForm({
      mission_id: '',
      objective_id: '',
      title: '',
      description: '',
      scope: '',
      responsible_email: '',
      consulted_emails: [],
      informed_emails: [],
      eisenhower_quadrant: 'not_urgent_important',
      key_results: [],
      due_date: '',
      skill_requirements: [],
      skill_rewards: [],
      principles_adhered: [],
      red_line_considerations: [],
      narrative_on_principles: '',
      status: undefined
    });
    setScopeSuggestion(null);
    setScopeValidation(null);
  };

  const [formErrors, setFormErrors] = useState([]);

  const handleSuggestScope = async () => {
    if (!activityForm.title) return;
    setScopeSuggesting(true);
    setScopeSuggestion(null);
    try {
      var result = await base44.functions.invoke('bullseyeScopeAgent', {
        action: 'suggest_scope',
        activity_data: {
          title: activityForm.title,
          description: activityForm.description,
          responsible_email: activityForm.responsible_email,
          key_results: activityForm.key_results
        }
      });
      var data = result?.data || result;
      if (data.error) {
        console.warn('Scope suggestion failed:', data.error);
      } else if (data.suggestion) {
        setScopeSuggestion(data.suggestion);
      }
    } catch (e) {
      console.warn('Scope suggestion error:', e.message);
    } finally {
      setScopeSuggesting(false);
    }
  };

  const handleApplyScopeSuggestion = () => {
    if (!scopeSuggestion) return;
    var updated = { ...activityForm, scope: scopeSuggestion.scope };
    // Apply dashboard_signal suggestions to KRs
    if (scopeSuggestion.kr_signals && scopeSuggestion.kr_signals.length > 0) {
      var krs = [...(activityForm.key_results || [])];
      scopeSuggestion.kr_signals.forEach(function(sig) {
        if (sig.signal_id && sig.kr_index >= 0 && sig.kr_index < krs.length) {
          krs[sig.kr_index] = { ...krs[sig.kr_index], dashboard_signal: sig.signal_id, direction: sig.direction || krs[sig.kr_index].direction || 'gte' };
        }
      });
      updated.key_results = krs;
    }
    setActivityForm(updated);
    setScopeSuggestion(null);
  };

  const handleValidateScope = async () => {
    var scope = activityForm.scope;
    if (!scope) return;
    setScopeValidating(true);
    setScopeValidation(null);
    try {
      var result = await base44.functions.invoke('bullseyeScopeAgent', {
        action: 'validate_scope',
        scope: scope
      });
      var data = result?.data || result;
      setScopeValidation(data);
    } catch (e) {
      setScopeValidation({ error: e.message });
    } finally {
      setScopeValidating(false);
    }
  };

  const validateForm = () => {
    const errors = [];
    if (!activityForm.mission_id) errors.push('Parent Mission is required');
    if (!activityForm.objective_id) errors.push('Parent Objective is required');
    if (!activityForm.title || !activityForm.title.trim()) errors.push('Activity Title is required');
    if (!activityForm.responsible_email) errors.push('Responsible (R) officer is required');
    if (!activityForm.due_date) errors.push('Due Date is required');
    if (!activityForm.key_results || activityForm.key_results.length === 0) {
      errors.push('At least one Key Result is required');
    } else {
      const hasEmptyKR = activityForm.key_results.some(kr => !kr.description || !kr.target);
      if (hasEmptyKR) errors.push('All Key Results must have a description and target value');
    }
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors([]);
    if (editingActivity) {
      updateActivityMutation.mutate({ id: editingActivity.id, data: activityForm });
    } else {
      createActivityMutation.mutate(activityForm);
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    const objective = objectives.find((o) => o.id === activity.objective_id);
    const mission = missions.find((m) => m.id === objective?.mission_id);
    setActivityForm({
      mission_id: mission?.id || '',
      objective_id: activity.objective_id,
      title: activity.title,
      description: activity.description || '',
      scope: activity.scope || '',
      responsible_email: activity.responsible_email,
      consulted_emails: activity.consulted_emails || [],
      informed_emails: activity.informed_emails || [],
      eisenhower_quadrant: activity.eisenhower_quadrant || 'not_urgent_important',
      key_results: activity.key_results || [],
      due_date: activity.due_date || '',
      skill_requirements: activity.skill_requirements || [],
      skill_rewards: activity.skill_rewards || [],
      principles_adhered: activity.principles_adhered || [],
      red_line_considerations: activity.red_line_considerations || [],
      narrative_on_principles: activity.narrative_on_principles || ''
    });
    setActivityDialog(true);
  };

  const handleArchiveClick = async (activity) => {
    const activityTasks = dailyTasks.filter(t => t.activity_id === activity.id);
    const deps = activityTasks.map(task => ({
      type: 'Daily Task',
      name: task.task_description,
      code: format(new Date(task.date), 'MMM d, yyyy'),
      page: 'dailytasks'
    }));
    setArchiveDependents(deps);
    setActivityToArchive(activity);
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = () => {
    if (activityToArchive) {
      archiveActivityMutation.mutate(activityToArchive.id);
    }
  };

  const handleDeleteClick = async (activity) => {
    const activityTasks = dailyTasks.filter(t => t.activity_id === activity.id);
    if (activityTasks.length > 0) {
      alert(`Cannot delete activity with ${activityTasks.length} existing task${activityTasks.length === 1 ? '' : 's'}. Please delete or archive tasks first.`);
      return;
    }
    if (confirm('Delete this activity permanently? This action cannot be undone.')) {
      deleteActivityMutation.mutate(activity.id);
    }
  };

  const addKeyResult = () => {
    setActivityForm({
      ...activityForm,
      key_results: [...(activityForm.key_results || []), { description: '', target: '', current_value: '0', unit: '', dashboard_signal: '', direction: 'gte', measurement_id: '' }]
    });
  };

  const updateKeyResult = (index, field, value) => {
    const updated = [...activityForm.key_results];
    updated[index] = { ...updated[index], [field]: value };
    setActivityForm({ ...activityForm, key_results: updated });
  };

  const removeKeyResult = (index) => {
    setActivityForm({
      ...activityForm,
      key_results: activityForm.key_results.filter((_, i) => i !== index)
    });
  };

  const openReportDialog = (activity) => {
    setSelectedActivity(activity);
    
    // Get latest progress report to pre-populate form
    const activityReports = progressReports.filter(r => r.activity_id === activity.id).sort((a, b) => new Date(a.reported_at) - new Date(b.reported_at));
    const latestReport = activityReports[activityReports.length - 1];
    
    // Get latest report's issues
    const latestReportIssues = latestReport ? reportIssues.filter(i => i.progress_report_id === latestReport.id) : [];
    
    setReportForm({
      key_results_progress: (activity.key_results || []).map((kr) => ({
        description: kr.description,
        new_value: kr.current_value || '0'
      })),
      issues_encountered: latestReportIssues.map(issue => ({
        issue_description: issue.issue_description,
        tagged_officers: issue.tagged_officers || [],
        tagged_artifacts: issue.tagged_artifacts || [],
        tagged_rituals: issue.tagged_rituals || [],
        tagged_policies: issue.tagged_policies || [],
        tagged_capabilities: issue.tagged_capabilities || []
      })),
      additional_context: latestReport?.additional_context || '',
      flagged_items: latestReport?.flagged_items || [],
      narrative_on_principles: latestReport?.narrative_on_principles || '',
      proposed_status_flag: activity.status_flag || 'yellow'
    });
    setReportDialog(true);
  };

  const addIssue = () => {
    setReportForm({
      ...reportForm,
      issues_encountered: [
      ...reportForm.issues_encountered,
      {
        issue_description: '',
        tagged_officers: [],
        tagged_artifacts: [],
        tagged_rituals: [],
        tagged_policies: [],
        tagged_capabilities: []
      }]

    });
  };

  const updateIssue = (index, field, value) => {
    const updated = [...reportForm.issues_encountered];
    updated[index] = { ...updated[index], [field]: value };
    setReportForm({ ...reportForm, issues_encountered: updated });
  };

  const removeIssue = (index) => {
    setReportForm({
      ...reportForm,
      issues_encountered: reportForm.issues_encountered.filter((_, i) => i !== index)
    });
  };

  const addTagToIssue = (issueIndex, tagType, value) => {
    const updated = [...reportForm.issues_encountered];
    
    // Map tag type to the correct field name
    const fieldMap = {
      'Officer': 'tagged_officers',
      'Artifact': 'tagged_artifacts',
      'Ritual': 'tagged_rituals',
      'Policy': 'tagged_policies',
      'Capability': 'tagged_capabilities'
    };
    const fieldName = fieldMap[tagType];
    
    // Check if already tagged to prevent duplicates
    if (updated[issueIndex][fieldName]?.includes(value)) {
      return;
    }
    
    updated[issueIndex] = {
      ...updated[issueIndex],
      [fieldName]: [...(updated[issueIndex][fieldName] || []), value]
    };
    setReportForm({ ...reportForm, issues_encountered: updated });
  };

  const removeTagFromIssue = (issueIndex, tagType, tagValue) => {
    const updated = [...reportForm.issues_encountered];
    updated[issueIndex] = {
      ...updated[issueIndex],
      [tagType]: updated[issueIndex][tagType].filter((t) => t !== tagValue)
    };
    setReportForm({ ...reportForm, issues_encountered: updated });
  };

  const removeTagFromReport = useMutation({
    mutationFn: async ({ issueIds, tagType, tagValue }) => {
      // Update multiple issues
      const updates = issueIds.map(issueId => {
        const issue = reportIssues.find((i) => i.id === issueId);
        const removedTags = issue.removed_tags || [];
        removedTags.push({
          tag_type: tagType,
          tag_value: tagValue,
          removed_by: user?.email,
          removed_at: new Date().toISOString()
        });

        return base44.entities.ReportIssue.update(issueId, {
          removed_tags: removedTags
        });
      });

      await Promise.all(updates);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reportIssues'] })
  });

  const reinstateTag = useMutation({
    mutationFn: async ({ issueId, tagType, tagValue }) => {
      const issue = reportIssues.find((i) => i.id === issueId);

      // Remove from removed_tags
      const removedTags = (issue.removed_tags || []).filter(
        (rt) => !(rt.tag_type === tagType && rt.tag_value === tagValue)
      );

      return base44.entities.ReportIssue.update(issueId, {
        removed_tags: removedTags
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reportIssues'] })
  });

  const removeFlag = useMutation({
    mutationFn: async ({ reportId, flagValue }) => {
      const report = progressReports.find((r) => r.id === reportId);

      const removedFlags = report.removed_flags || [];
      removedFlags.push({
        flag_value: flagValue,
        removed_by: user?.email,
        removed_at: new Date().toISOString()
      });

      return base44.entities.ProgressReport.update(reportId, {
        removed_flags: removedFlags
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['progressReports'] })
  });

  const reinstateFlag = useMutation({
    mutationFn: async ({ reportId, flagValue }) => {
      const report = progressReports.find((r) => r.id === reportId);

      const removedFlags = (report.removed_flags || []).filter(
        (rf) => rf.flag_value !== flagValue
      );

      return base44.entities.ProgressReport.update(reportId, {
        removed_flags: removedFlags
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['progressReports'] })
  });

  // Get all unique tags across all reports for an activity (including struggling flags)
  const getAllTagsForActivity = (activity) => {
    const tagMap = {};
    const strugglingFlagsMap = {};
    const activityReports = progressReports.filter(r => r.activity_id === activity.id);

    activityReports.forEach((report) => {
      // Add struggling flags that haven't been removed
      const removedFlags = report.removed_flags || [];
      (report.flagged_items || []).forEach((item) => {
        const isRemoved = removedFlags.some((rf) => rf.flag_value === item);
        if (!isRemoved && !strugglingFlagsMap[item]) {
          strugglingFlagsMap[item] = { flagValue: item, reportId: report.id };
        }
      });
    });

    const activityIssues = reportIssues.filter(issue => {
      const report = progressReports.find(r => r.id === issue.progress_report_id);
      return report && report.activity_id === activity.id;
    });

    activityIssues.forEach((issue) => {
      const removedTags = issue.removed_tags || [];

      ['tagged_officers', 'tagged_artifacts', 'tagged_rituals', 'tagged_policies', 'tagged_capabilities'].forEach((tagType) => {
        (issue[tagType] || []).forEach((tag) => {
          const isRemoved = removedTags.some((rt) => rt.tag_type === tagType && rt.tag_value === tag);
          if (!isRemoved) {
            const key = `${tagType}:${tag}`;
            if (!tagMap[key]) {
              tagMap[key] = { tagType, tagValue: tag, issueId: issue.id };
            }
          }
        });
      });
    });

    return { tags: Object.values(tagMap), strugglingFlags: Object.values(strugglingFlagsMap) };
  };

  const eisenhowerLabels = {
    urgent_important: { label: 'Do First', color: 'bg-red-500/20 text-red-400', priority: 'Critical' },
    not_urgent_important: { label: 'Schedule', color: 'bg-indigo-500/20 text-indigo-400', priority: 'High' },
    urgent_not_important: { label: 'Delegate', color: 'bg-amber-500/20 text-amber-400', priority: 'Medium' },
    not_urgent_not_important: { label: 'Eliminate', color: 'bg-slate-500/20 text-slate-400', priority: 'Low' }
  };

  // Group activities by selected mode and sort by display_order
  const groupedActivities = filteredActivities.reduce((acc, activity) => {
    const groupKey = groupByMode === 'accountable' ? activity.accountable_email : activity.responsible_email;
    if (!groupKey) return acc; // Skip if no group key
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(activity);
    return acc;
  }, {});

  // Sort activities within each group by display_order
  Object.keys(groupedActivities).forEach((key) => {
    groupedActivities[key].sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
  });

  const handleDragEnd = (result, groupEmail) => {
    if (!result.destination) return;

    const items = Array.from(groupedActivities[groupEmail]);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for smooth UX
    groupedActivities[groupEmail] = items;

    // Persist to backend
    const activityIds = items.map((item) => item.id);
    reorderActivitiesMutation.mutate({ activityIds });
  };

  // Helper component for officer display with hover and copy functionality
  const OfficerDisplay = ({ email, showLabel = false, label = '' }) => {
    const officer = officers.find((o) => o.user_email === email);
    const displayName = officer ?
    `${officer.callsign} - ${officer.rank_title} (L${officer.clearance_level})` :
    email;

    const handleCopy = () => {
      navigator.clipboard.writeText(email);
    };

    return (
      <div className="inline-flex items-center gap-1">
        {showLabel && <span className="font-semibold">{label}: </span>}
        <button
          onClick={handleCopy}
          title={`Email: ${email}\nClick to copy`}
          className="text-slate-300 hover:text-indigo-400 transition-colors cursor-pointer underline decoration-dotted">

          {displayName}
        </button>
      </div>);

  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Activities</h1>
          <p className="text-slate-400">Track activities spawned from objectives with RACI and key results</p>
        </div>
        <div className="flex gap-4 flex-wrap">
          <div className="flex gap-2 bg-slate-900/50 rounded-lg p-1 border border-slate-800">
            <Button
              size="sm"
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}>

              <LayoutGrid className="w-4 h-4 mr-2" />
              Table
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'gantt' ? 'default' : 'ghost'}
              onClick={() => setViewMode('gantt')}
              className={viewMode === 'gantt' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}>

              <Calendar className="w-4 h-4 mr-2" />
              Gantt
            </Button>
          </div>

          {/* Mission Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-48 bg-slate-950 border-slate-800 text-slate-100 justify-between flex-shrink-0">
                {missionFilters.length === 0 ? "Filter by Mission" : `${missionFilters.length} selected`}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-60 p-0 bg-slate-950 border-slate-800">
              <Command className="bg-slate-950 text-slate-100">
                <CommandInput placeholder="Search missions..." className="h-9" />
                <CommandList>
                  <CommandEmpty>No mission found.</CommandEmpty>
                  <CommandGroup>
                    {(missions || []).map((mission) => (
                      <CommandItem
                        key={mission.id}
                        value={`${mission.mission_code || ''} ${mission.title}`}
                        onSelect={() => {
                          setMissionFilters(prev => 
                            prev.includes(mission.id) 
                              ? prev.filter(id => id !== mission.id)
                              : [...prev, mission.id]
                          );
                        }}
                        className="text-slate-100 aria-selected:bg-slate-800 aria-selected:text-slate-100 cursor-pointer"
                      >
                        <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary ${missionFilters.includes(mission.id) ? "bg-indigo-600 border-indigo-600" : "opacity-50 [&_svg]:invisible"}`}>
                          <Check className="h-3 w-3" />
                        </div>
                        {mission.mission_code && <span className="text-indigo-400 mr-1">[{mission.mission_code}]</span>}{mission.title}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Objective Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-48 bg-slate-950 border-slate-800 text-slate-100 justify-between flex-shrink-0">
                {objectiveFilters.length === 0 ? "Filter by Objective" : `${objectiveFilters.length} selected`}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 bg-slate-950 border-slate-800">
              <Command className="bg-slate-950 text-slate-100">
                <CommandInput placeholder="Search objectives..." className="h-9" />
                <CommandList>
                  <CommandEmpty>No objective found.</CommandEmpty>
                  <CommandGroup>
                    {(objectives || [])
                      .filter(obj => missionFilters.length === 0 || missionFilters.includes(obj.mission_id))
                      .map((objective) => (
                      <CommandItem
                        key={objective.id}
                        value={objective.statement}
                        onSelect={() => {
                          setObjectiveFilters(prev => 
                            prev.includes(objective.id) 
                              ? prev.filter(id => id !== objective.id)
                              : [...prev, objective.id]
                          );
                        }}
                        className="text-slate-100 aria-selected:bg-slate-800 aria-selected:text-slate-100 cursor-pointer"
                      >
                        <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary ${objectiveFilters.includes(objective.id) ? "bg-indigo-600 border-indigo-600" : "opacity-50 [&_svg]:invisible"}`}>
                          <Check className="h-3 w-3" />
                        </div>
                        <div className="truncate text-xs">
                          {objective.objective_code && <span className="text-indigo-400 mr-1">[{objective.objective_code}]</span>}
                          {objective.statement}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Select value={groupByMode} onValueChange={setGroupByMode}>
            <SelectTrigger className="w-48 bg-slate-950 border-slate-800 text-slate-100 flex-shrink-0">
              <SelectValue placeholder="Group by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="accountable">Group by Accountable</SelectItem>
              <SelectItem value="responsible">Group by Responsible</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-slate-950 border-slate-800 text-slate-100 flex-shrink-0">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <ConfirmableDialog open={activityDialog} onOpenChange={(open) => {
            setActivityDialog(open);
            if (!open) {
              setEditingActivity(null);
              resetForm();
              setFormErrors([]);
            }
          }}>
            <DialogTrigger asChild>
              <div className="flex gap-2 flex-shrink-0">
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  New Activity
                </Button>
                <Button 
                  className="bg-slate-700 hover:bg-slate-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivityForm({ ...activityForm, status: 'draft' });
                    setActivityDialog(true);
                  }}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Draft
                </Button>
              </div>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingActivity ? 'Edit Activity' : 'Create New Activity'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="space-y-2">
                  <Label>Parent Mission *</Label>
                  <SearchableTagSelect
                    options={availableMissions.filter(m => m.id).map(mission => {
                      const isRelevant = mission.owner_email === user?.email || mission.sponsor_email === user?.email;
                      return {
                        value: mission.id,
                        label: `${isRelevant ? '⭐ ' : ''}${mission.status === 'archived' ? '[archived!] ' : ''}${mission.mission_code ? `[${mission.mission_code}] ` : ''}${mission.title}`
                      };
                    })}
                    value={activityForm.mission_id}
                    onValueChange={(value) => setActivityForm({ ...activityForm, mission_id: value, objective_id: '' })}
                    placeholder="Select mission..."
                    emptyMessage="No missions found"
                    className="bg-slate-950 border-slate-800"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Parent Objective *</Label>
                  <SearchableTagSelect
                    options={(activityForm.mission_id 
                      ? objectives.filter((obj) => obj.mission_id === activityForm.mission_id && obj.id && (editingActivity || obj.status === 'active'))
                      : []
                    ).map(obj => {
                      const isRelevant = obj.owner_email === user?.email;
                      return {
                        value: obj.id,
                        label: `${isRelevant ? '⭐ ' : ''}${obj.status === 'archived' ? '[archived!] ' : ''}${obj.objective_code ? `[${obj.objective_code}] ` : ''}${obj.statement}`
                      };
                    })}
                    value={activityForm.objective_id}
                    onValueChange={(value) => setActivityForm({ ...activityForm, objective_id: value })}
                    placeholder={activityForm.mission_id ? "Select objective..." : "Select mission first..."}
                    emptyMessage="No objectives found"
                    disabled={!activityForm.mission_id}
                    className="bg-slate-950 border-slate-800"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Activity Title</Label>
                  <Textarea
                    value={activityForm.title}
                    onChange={(e) => {
                      setActivityForm({ ...activityForm, title: e.target.value });
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    onFocus={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[44px] resize-none overflow-hidden"
                    required />

                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={activityForm.description}
                    onChange={(e) => {
                      setActivityForm({ ...activityForm, description: e.target.value });
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    onFocus={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[60px] resize-none overflow-hidden"
                    placeholder="Describe the activity..." />

                </div>

                {/* ── BULLSEYE: Scope Field ── */}
                <div className="p-4 bg-gradient-to-br from-sky-500/5 to-cyan-500/5 rounded-xl border border-sky-500/20 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-sky-400" />
                      <Label className="text-sm font-semibold text-sky-300">Bullseye Scope</Label>
                    </div>
                    <Button type="button" size="sm" variant="outline"
                      className="border-sky-500/30 text-sky-400 hover:bg-sky-500/10 h-7 text-xs"
                      onClick={handleSuggestScope}
                      disabled={scopeSuggesting || !activityForm.title}>
                      {scopeSuggesting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Suggesting...</> : '🤖 AI Suggest'}
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    <Input
                      value={activityForm.scope}
                      onChange={(e) => setActivityForm({ ...activityForm, scope: e.target.value })}
                      placeholder='e.g., "TPA-008-01" or "content=BurgerFi, msa=Dallas" or "all"'
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 text-sm"
                      onBlur={activityForm.scope ? handleValidateScope : undefined}
                    />
                    <p className="text-[10px] text-slate-500">Which vessels does this activity apply to? Single vessel code, filter expression (content=X, msa=Y), or "all" for network-wide.</p>
                  </div>

                  {/* Scope validation result */}
                  {scopeValidation && !scopeValidation.error && (
                    <div className={`px-3 py-2 rounded-lg text-xs ${scopeValidation.vessel_count === 0 ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                      {scopeValidation.vessel_count === 'all' ? '🌐 Network-wide — applies to all active vessels' :
                        scopeValidation.vessel_count === 0 ? '⚠️ No vessels matched this scope — check the filter' :
                        '✓ ' + scopeValidation.vessel_count + ' vessel' + (scopeValidation.vessel_count !== 1 ? 's' : '') + ' matched' +
                        (scopeValidation.vessels && scopeValidation.vessels.length > 0 ? ': ' + scopeValidation.vessels.slice(0, 5).map(function(v) { return v.vessel_code; }).join(', ') + (scopeValidation.vessel_count > 5 ? '...' : '') : '')}
                    </div>
                  )}

                  {/* AI Scope Suggestion */}
                  {scopeSuggestion && (
                    <div className="p-3 bg-sky-500/5 border border-sky-500/20 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-sky-400">🤖 AI Suggestion</span>
                        <div className="flex gap-1.5">
                          <Button type="button" size="sm" className="h-6 px-3 text-[10px] bg-sky-600 hover:bg-sky-700 text-white" onClick={handleApplyScopeSuggestion}>
                            Apply All
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-slate-500" onClick={() => setScopeSuggestion(null)}>
                            Dismiss
                          </Button>
                        </div>
                      </div>
                      <div className="text-xs text-slate-300">
                        <strong>Scope:</strong> <code className="bg-slate-800 px-1.5 py-0.5 rounded text-sky-300">{scopeSuggestion.scope}</code>
                        <span className="text-slate-500 ml-2">{scopeSuggestion.scope_reasoning}</span>
                      </div>
                      {scopeSuggestion.kr_signals && scopeSuggestion.kr_signals.length > 0 && (
                        <div className="space-y-1 mt-1">
                          <span className="text-[10px] text-slate-500 font-bold">KR → Dashboard Signal Mapping:</span>
                          {scopeSuggestion.kr_signals.map(function(sig, i) {
                            return (
                              <div key={i} className="flex items-center gap-2 text-[10px] py-0.5">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${sig.match_confidence === 'high' ? 'bg-emerald-400' : sig.match_confidence === 'medium' ? 'bg-amber-400' : sig.match_confidence === 'low' ? 'bg-orange-400' : 'bg-slate-600'}`} />
                                <span className="text-slate-400 truncate flex-1">{sig.kr_description}</span>
                                <span className="text-slate-500">→</span>
                                {sig.display_label
                                  ? <span className="text-sky-300 font-semibold">{sig.display_label}</span>
                                  : <span className="text-slate-600 italic">no match</span>}
                                {sig.needs_derived && <span className="text-amber-400 text-[9px] font-bold">⚠ needs derived</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="p-4 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 rounded-xl border border-indigo-500/20">
                  <SkillRequirementsManager
                    requirements={activityForm.skill_requirements || []}
                    rewards={activityForm.skill_rewards || []}
                    onChange={({ requirements, rewards }) =>
                    setActivityForm({
                      ...activityForm,
                      skill_requirements: requirements,
                      skill_rewards: rewards
                    })
                    } />

                </div>

                <div className="p-4 bg-gradient-to-br from-amber-500/5 to-orange-500/5 rounded-xl border border-amber-500/20">
                  <Collapsible defaultOpen={false}>
                    <CollapsibleTrigger asChild>
                      <button type="button" className="flex items-center justify-between w-full text-left">
                        <div className="flex items-center gap-2">
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                          <Target className="w-4 h-4 text-amber-400" />
                          <Label className="text-sm font-semibold text-slate-300 cursor-pointer">Priority Matrix</Label>
                        </div>
                        <Badge className={`${eisenhowerLabels[activityForm.eisenhower_quadrant]?.color || 'bg-slate-500/20 text-slate-400'} text-xs`}>
                          {eisenhowerLabels[activityForm.eisenhower_quadrant]?.label || 'Schedule'}
                        </Badge>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div
                          onClick={() => setActivityForm({ ...activityForm, eisenhower_quadrant: 'urgent_important' })}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          activityForm.eisenhower_quadrant === 'urgent_important' ?
                          'border-red-500 bg-red-500/10' :
                          'border-slate-800 bg-slate-950 hover:border-slate-700'}`
                          }>
                          <div className="font-semibold text-slate-200 mb-1">Urgent + Important</div>
                          <div className="text-xs text-slate-400">Do it now yourself</div>
                        </div>
                        <div
                          onClick={() => setActivityForm({ ...activityForm, eisenhower_quadrant: 'urgent_not_important' })}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          activityForm.eisenhower_quadrant === 'urgent_not_important' ?
                          'border-amber-500 bg-amber-500/10' :
                          'border-slate-800 bg-slate-950 hover:border-slate-700'}`
                          }>
                          <div className="font-semibold text-slate-200 mb-1">Urgent + Not Important</div>
                          <div className="text-xs text-slate-400">Delegate to junior person</div>
                        </div>
                        <div
                          onClick={() => setActivityForm({ ...activityForm, eisenhower_quadrant: 'not_urgent_important' })}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          activityForm.eisenhower_quadrant === 'not_urgent_important' ?
                          'border-indigo-500 bg-indigo-500/10' :
                          'border-slate-800 bg-slate-950 hover:border-slate-700'}`
                          }>
                          <div className="font-semibold text-slate-200 mb-1">Not Urgent + Important</div>
                          <div className="text-xs text-slate-400">Delegate to senior or schedule yourself</div>
                        </div>
                        <div
                          onClick={() => setActivityForm({ ...activityForm, eisenhower_quadrant: 'not_urgent_not_important' })}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          activityForm.eisenhower_quadrant === 'not_urgent_not_important' ?
                          'border-slate-500 bg-slate-500/10' :
                          'border-slate-800 bg-slate-950 hover:border-slate-700'}`
                          }>
                          <div className="font-semibold text-slate-200 mb-1">Not Urgent + Not Important</div>
                          <div className="text-xs text-slate-400">Don't do it</div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>

                <div className="p-4 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-xl border border-cyan-500/20">
                <Collapsible defaultOpen={!!activityForm.responsible_email || (activityForm.consulted_emails || []).length > 0 || (activityForm.informed_emails || []).length > 0}>
                  <CollapsibleTrigger asChild>
                    <button type="button" className="flex items-center justify-between w-full text-left">
                      <div className="flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                        <Users className="w-4 h-4 text-cyan-400" />
                        <Label className="text-sm font-semibold text-slate-300 cursor-pointer">RACI Assignment</Label>
                      </div>
                      <div className="flex flex-wrap gap-1 max-w-[60%] justify-end">
                        {activityForm.responsible_email && (() => {
                          const o = officers.find(of => of.user_email === activityForm.responsible_email);
                          return <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30">R: {o?.callsign || activityForm.responsible_email}</Badge>;
                        })()}
                        {(activityForm.consulted_emails || []).length > 0 && <Badge variant="outline" className="text-[10px] text-cyan-400 border-cyan-500/30">C: {activityForm.consulted_emails.length}</Badge>}
                        {(activityForm.informed_emails || []).length > 0 && <Badge variant="outline" className="text-[10px] text-purple-400 border-purple-500/30">I: {activityForm.informed_emails.length}</Badge>}
                        {!activityForm.responsible_email && (activityForm.consulted_emails || []).length === 0 && (activityForm.informed_emails || []).length === 0 && <span className="text-xs text-slate-500 italic">Not assigned</span>}
                      </div>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4 space-y-4">

                <div className="space-y-2">
                  <Label>Responsible (R)</Label>
                  {activityForm.eisenhower_quadrant === 'urgent_important' &&
                  <div className="text-xs text-red-400 mb-2 p-2 bg-red-500/10 rounded border border-red-500/30">
                      💡 Suggestion: Assign to yourself (accountable person) to handle immediately
                    </div>
                  }
                  {activityForm.eisenhower_quadrant === 'urgent_not_important' &&
                  <div className="text-xs text-amber-400 mb-2 p-2 bg-amber-500/10 rounded border border-amber-500/30">
                      💡 Suggestion: Delegate to a junior team member who can handle this quickly
                    </div>
                  }
                  {activityForm.eisenhower_quadrant === 'not_urgent_important' &&
                  <div className="text-xs text-indigo-400 mb-2 p-2 bg-indigo-500/10 rounded border border-indigo-500/30">
                      💡 Suggestion: Delegate to a senior person or schedule for yourself when bandwidth allows
                    </div>
                  }
                  {activityForm.eisenhower_quadrant === 'not_urgent_not_important' &&
                  <div className="text-xs text-slate-400 mb-2 p-2 bg-slate-800/50 rounded border border-slate-700">
                      💡 Suggestion: Consider not doing this activity at all
                    </div>
                  }
                  <Select value={activityForm.responsible_email} onValueChange={(value) => setActivityForm({ ...activityForm, responsible_email: value })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800">
                      <SelectValue placeholder="Select officer..." />
                    </SelectTrigger>
                    <SelectContent>
                      {officers.filter(o => {
                        if (!o.user_email) return false;
                        if (!o.is_bot) return true;
                        return o.bot_owner_email === user?.email;
                      }).map((officer) => {
                        const isRelevant = officer.user_email === user?.email;
                        const isAI = officer.custom_role === 'AI Officer' || officer.is_bot;
                        const displayName = isAI 
                          ? `🤖 ${officer.callsign} (AI Officer)`
                          : `${officer.callsign}, ${officer.rank_title}, L${officer.clearance_level}`;
                        return (
                          <SelectItem 
                            key={officer.id} 
                            value={officer.user_email}
                            className={isRelevant ? 'bg-indigo-500/20 text-indigo-300 font-semibold' : ''}
                          >
                            {isRelevant && '⭐ '}{displayName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Accountable (A)</Label>
                  <div className="p-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300">
                    {(() => {
                      const objective = objectives.find((o) => o.id === activityForm.objective_id);
                      const accountableEmail = objective?.owner_email || user?.email;
                      const accountableOfficer = officers.find((o) => o.user_email === accountableEmail);
                      return accountableOfficer ? 
                        `${accountableOfficer.callsign}, ${accountableOfficer.rank_title}, L${accountableOfficer.clearance_level}` : 
                        accountableEmail;
                    })()}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Consulted (C)</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-950 border border-slate-800 rounded-lg min-h-[42px]">
                    {(activityForm.consulted_emails || []).map((email) => {
                      const officer = officers.find((o) => o.user_email === email);
                      return (
                        <Badge key={email} variant="secondary" className="bg-slate-100 text-secondary-foreground px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80">
                          {officer?.custom_role === 'AI Officer' ? `🤖 ${officer.callsign}` : officer?.callsign || email}
                          <button
                            type="button"
                            onClick={() => setActivityForm({
                              ...activityForm,
                              consulted_emails: activityForm.consulted_emails.filter((e) => e !== email)
                            })}
                            className="ml-1 hover:text-red-400">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>);
                    })}
                  </div>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      if (!activityForm.consulted_emails.includes(value)) {
                        setActivityForm({
                          ...activityForm,
                          consulted_emails: [...(activityForm.consulted_emails || []), value]
                        });
                      }
                    }}>
                    <SelectTrigger className="bg-slate-950 border-slate-800">
                      <SelectValue placeholder="Add officer to consult..." />
                    </SelectTrigger>
                    <SelectContent>
                      {officers.filter((o) =>
                      !activityForm.consulted_emails?.includes(o.user_email) &&
                      o.user_email !== activityForm.responsible_email &&
                      o.user_email
                      ).map((officer) => {
                        const isRelevant = officer.user_email === user?.email;
                        const isAI = officer.custom_role === 'AI Officer' || officer.is_bot;
                        const displayName = isAI
                          ? `🤖 ${officer.callsign} (AI Officer)`
                          : `${officer.callsign}, ${officer.rank_title}, L${officer.clearance_level}`;
                        return (
                          <SelectItem 
                            key={officer.id} 
                            value={officer.user_email}
                            className={isRelevant ? 'bg-indigo-500/20 text-indigo-300 font-semibold' : ''}
                          >
                            {isRelevant && '⭐ '}{displayName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Informed (I)</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-950 border border-slate-800 rounded-lg min-h-[42px]">
                    {(activityForm.informed_emails || []).map((email) => {
                      const officer = officers.find((o) => o.user_email === email);
                      return (
                        <Badge key={email} variant="secondary" className="bg-slate-50 text-secondary-foreground px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80">
                          {officer?.callsign || email}
                          <button
                            type="button"
                            onClick={() => setActivityForm({
                              ...activityForm,
                              informed_emails: activityForm.informed_emails.filter((e) => e !== email)
                            })}
                            className="ml-1 hover:text-red-400">
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>);
                    })}
                  </div>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      if (!activityForm.informed_emails.includes(value)) {
                        setActivityForm({
                          ...activityForm,
                          informed_emails: [...(activityForm.informed_emails || []), value]
                        });
                      }
                    }}>
                    <SelectTrigger className="bg-slate-950 border-slate-800">
                      <SelectValue placeholder="Add officer to inform..." />
                    </SelectTrigger>
                    <SelectContent>
                      {officers.filter((o) =>
                      !activityForm.informed_emails?.includes(o.user_email) &&
                      o.user_email !== activityForm.responsible_email &&
                      o.user_email
                      ).map((officer) => {
                        const isRelevant = officer.user_email === user?.email;
                        const isAI = officer.custom_role === 'AI Officer' || officer.is_bot;
                        const displayName = isAI
                          ? `🤖 ${officer.callsign} (AI Officer)`
                          : `${officer.callsign}, ${officer.rank_title}, L${officer.clearance_level}`;
                        return (
                          <SelectItem 
                            key={officer.id} 
                            value={officer.user_email}
                            className={isRelevant ? 'bg-indigo-500/20 text-indigo-300 font-semibold' : ''}
                          >
                            {isRelevant && '⭐ '}{displayName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                  </CollapsibleContent>
                </Collapsible>
                </div>

                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={activityForm.due_date}
                    onChange={(e) => setActivityForm({ ...activityForm, due_date: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500" />

                </div>

                <div className="space-y-3 p-4 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-xl border-2 border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.1)]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-emerald-400" />
                      <Label className="text-base font-bold text-emerald-300">Key Results ({(activityForm.key_results || []).length})</Label>
                    </div>
                    <Button type="button" size="sm" variant="outline" onClick={addKeyResult} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                      <Plus className="w-3 h-3 mr-1" />
                      Add Another
                    </Button>
                  </div>
                  {(activityForm.key_results || []).length === 0 && (
                    <button
                      type="button"
                      onClick={addKeyResult}
                      className="w-full p-4 border-2 border-dashed border-slate-700 rounded-lg text-slate-500 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors text-sm flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Click to add your first key result
                    </button>
                  )}
                  {(activityForm.key_results || []).map((kr, idx) =>
                  <div key={idx} className="p-3 bg-slate-950 rounded-lg border border-slate-800 space-y-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-semibold text-slate-500">Key Result #{idx + 1}</span>
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeKeyResult(idx)} className="h-6 w-6">
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </Button>
                      </div>
                      {/* Bullseye: Measurement Dropdown */}
                      <div className="space-y-1">
                        <Label className="text-[10px] text-amber-400 mb-1 block">🎯 Measurement</Label>
                        <MeasurementCombobox
                          value={kr.measurement_id || ''}
                          onChange={(vocabId, selected) => {
                            const updates = { measurement_id: vocabId };
                            if (selected && vocabId !== 'm_custom') {
                              updates.unit = selected.unit || kr.unit;
                              updates.direction = selected.direction || kr.direction;
                              if (selected.dashboard_signal) updates.dashboard_signal = selected.dashboard_signal;
                            }
                            const updated = [...activityForm.key_results];
                            updated[idx] = { ...updated[idx], ...updates };
                            setActivityForm({ ...activityForm, key_results: updated });
                          }}
                          measurements={measurements}
                          categories={measurementCategories}
                          allowedTiers={TIER_FILTERS.activity}
                          className="bg-slate-900 border-slate-700 h-8 w-full text-xs"
                        />
                      </div>
                      <Input
                        placeholder="Key result description"
                        value={kr.description}
                        onChange={(e) => updateKeyResult(idx, 'description', e.target.value)}
                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500" />
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs text-slate-500 mb-1 block">Target Value</Label>
                          <FormattedNumberInput
                          placeholder="e.g., 3"
                          value={kr.target}
                          onChange={(v) => updateKeyResult(idx, 'target', v)}
                          className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 text-sm" />
                        </div>

                        <div>
                          <Label className="text-xs text-slate-500 mb-1 block">Current Value</Label>
                          <FormattedNumberInput
                          placeholder="e.g., 0"
                          value={kr.current_value}
                          onChange={(v) => updateKeyResult(idx, 'current_value', v)}
                          className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 text-sm" />
                        </div>

                        <div>
                          <Label className="text-xs text-slate-500 mb-1 block">Unit</Label>
                          <Input
                          placeholder="e.g., people"
                          value={kr.unit}
                          onChange={(e) => updateKeyResult(idx, 'unit', e.target.value)}
                          className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 text-sm" />
                        </div>
                      </div>
                      {/* Bullseye: Dashboard Signal + Direction */}
                      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800/50">
                        <div>
                          <Label className="text-[10px] text-sky-400 mb-1 block">Dashboard Signal</Label>
                          <Input
                            placeholder="e.g., sales_avg_7d (or use AI Suggest)"
                            value={kr.dashboard_signal || ''}
                            onChange={(e) => updateKeyResult(idx, 'dashboard_signal', e.target.value)}
                            className="bg-slate-900 border-slate-700 text-slate-200 placeholder:text-slate-600 text-xs h-7" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-sky-400 mb-1 block">Direction</Label>
                          <select
                            value={kr.direction || 'gte'}
                            onChange={(e) => updateKeyResult(idx, 'direction', e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-md h-7 px-2 text-xs">
                            <option value="gte">↑ Higher is better (≥ target)</option>
                            <option value="lte">↓ Lower is better (≤ target)</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <PrinciplesSection
                  principlesAdhered={activityForm.principles_adhered}
                  redLineConsiderations={activityForm.red_line_considerations}
                  narrative={activityForm.narrative_on_principles}
                  onPrinciplesChange={(principles) => setActivityForm({ ...activityForm, principles_adhered: principles })}
                  onRedLinesChange={(redLines) => setActivityForm({ ...activityForm, red_line_considerations: redLines })}
                  onNarrativeChange={(narrative) => setActivityForm({ ...activityForm, narrative_on_principles: narrative })} />

                {formErrors.length > 0 && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg space-y-1">
                    <div className="text-sm font-semibold text-red-400">Please fix the following:</div>
                    {formErrors.map((err, idx) => (
                      <div key={idx} className="text-xs text-red-300 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 flex-shrink-0" /> {err}
                      </div>
                    ))}
                  </div>
                )}

                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                  {editingActivity ? 'Update Activity' : 'Create Activity'}
                </Button>
              </form>
            </DialogContent>
          </ConfirmableDialog>
        </div>
      </div>

      {/* Activities View - Table or Gantt */}
      {viewMode === 'gantt' ?
      <DragDropContext onDragEnd={(result) => {
        if (!result.destination) return;
        const groupEmail = result.destination.droppableId;
        handleDragEnd(result, groupEmail);
      }}>
          <GanttChart
          groupedActivities={groupedActivities}
          officers={officers}
          objectives={objectives}
          missions={missions}
          expandedActivities={expandedOfficers}
          setExpandedActivities={setExpandedOfficers}
          handleEdit={handleEdit}
          deleteActivityMutation={deleteActivityMutation}
          archiveActivityMutation={archiveActivityMutation}
          unarchiveActivityMutation={unarchiveActivityMutation}
          updateKeyResultsStatusMutation={updateKeyResultsProgressMutation}
          user={user}
          isCommander={isCommander}
          eisenhowerLabels={eisenhowerLabels}
          groupByMode={groupByMode} />

        </DragDropContext> :

      <div className="overflow-x-auto">
        {Object.keys(groupedActivities).length === 0 ?
        <Card className="glass-panel border-none">
            <CardContent className="py-12 text-center">
              <Activity className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg mb-2">No activities found</p>
              <p className="text-sm text-slate-600">Create an activity from an active objective to get started</p>
            </CardContent>
          </Card> :

        Object.entries(groupedActivities).map(([groupEmail, groupActivities]) => {
          const groupOfficer = officers.find((o) => o.user_email === groupEmail);
          const activityCount = groupActivities.length;
          const liveCount = activities.filter((a) => { const k = groupByMode === 'accountable' ? a.accountable_email : a.responsible_email; return k === groupEmail && a.status !== 'archived'; }).length;

          return (
            <div key={groupEmail} className="mb-8">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-white">
                      {groupOfficer ? `${groupOfficer.callsign} - ${groupOfficer.rank_title} (L${groupOfficer.clearance_level})` : groupEmail}
                    </h2>
                    <Badge variant="outline" className="text-slate-400 border-slate-700">
                      {liveCount} Live{activityCount !== liveCount ? ` (${activityCount} shown)` : ''} {liveCount === 1 ? 'Activity' : 'Activities'}
                    </Badge>

                  </div>
                </div>

                {/* Activities Table */}
                <DragDropContext onDragEnd={(result) => handleDragEnd(result, groupEmail)}>
                  <div className="bg-slate-900/50 rounded-xl border border-slate-800 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <Droppable droppableId={groupEmail} direction="horizontal">
                          {(provided) =>
                        <thead className="bg-slate-950/50 border-b border-slate-800" ref={provided.innerRef} {...provided.droppableProps}>
                              <tr>
                                {groupActivities.map((activity, index) =>
                            <Draggable key={activity.id} draggableId={activity.id} index={index}>
                                    {(provided, snapshot) =>
                              <ActivityHeaderCell
                                activity={activity}
                                provided={provided}
                                snapshot={snapshot}
                                groupEmail={groupEmail}
                                groupByMode={groupByMode}
                                expandedOfficers={expandedOfficers}
                                setExpandedOfficers={setExpandedOfficers}
                                eisenhowerLabels={eisenhowerLabels}
                                officers={officers}
                                dailyTasks={dailyTasks}
                                user={user}
                                handleEdit={handleEdit}
                                handleArchiveClick={handleArchiveClick}
                                handleDeleteClick={handleDeleteClick}
                                unarchiveActivityMutation={unarchiveActivityMutation}
                              />
                              }
                                  </Draggable>
                            )}
                                {provided.placeholder}
                              </tr>
                            </thead>
                        }
                        </Droppable>
                        <tbody>
                        {/* Objective Row */}
                        <tr className="border-b border-slate-800">
                          {groupActivities.map((activity) => {
                            if (!expandedOfficers[groupEmail]) return null;
                            const objective = objectives.find((o) => o.id === activity.objective_id);
                            return (
                              <td key={activity.id} className="p-4 align-top border-r border-slate-800 last:border-r-0 bg-slate-950/30">
                                {objective && objective.status === 'archived' && (
                                  <div className="mb-2">
                                    <ArchivedParentBadge 
                                      parentType="Objective"
                                      parentName={objective.statement}
                                      parentPage="discovery"
                                    />
                                  </div>
                                )}
                                {objective && (() => {
                                  const mission = missions.find(m => m.id === objective.mission_id);
                                  return mission && mission.status === 'archived' ? (
                                    <div className="mb-2">
                                      <ArchivedParentBadge 
                                        parentType="Mission"
                                        parentName={mission.title}
                                        parentPage="missions"
                                      />
                                    </div>
                                  ) : null;
                                })()}
                                <div className="text-xs text-slate-500 font-semibold mb-1">OBJECTIVE</div>
                                <div className="text-sm text-slate-300">
                                  {objective ?
                                  <>
                                      {objective.objective_code && <span className="text-indigo-400">[{objective.objective_code}]</span>} {objective.statement}
                                    </> :
                                  'No objective'}
                                </div>
                              </td>);

                          })}
                        </tr>

                        {/* Description Row */}
                        <tr className="border-b border-slate-800">
                          {groupActivities.map((activity) => {
                            if (!expandedOfficers[groupEmail]) return null;
                            return (
                              <td key={activity.id} className="p-4 align-top border-r border-slate-800 last:border-r-0">
                                <div className="text-xs text-slate-500 font-semibold mb-1">DESCRIPTION</div>
                                <div className="text-sm text-slate-300">
                                  {activity.description || <span className="text-slate-600 italic">No description</span>}
                                </div>
                              </td>);

                          })}
                        </tr>

                        {/* RACI Row */}
                        <tr className="border-b border-slate-800">
                          {groupActivities.map((activity) => {
                            if (!expandedOfficers[groupEmail]) return null;
                            return (
                              <td key={activity.id} className="p-4 align-top border-r border-slate-800 last:border-r-0 bg-slate-950/30">
                                <div className="space-y-2 text-sm">
                                  <div>
                                    <span className="text-xs text-indigo-400 font-semibold">A: </span>
                                    <OfficerDisplay email={activity.accountable_email} />
                                  </div>
                                  <div>
                                    <span className="text-xs text-emerald-400 font-semibold">R: </span>
                                    {activity.responsible_email ?
                                    <OfficerDisplay email={activity.responsible_email} /> :

                                    <span className="text-slate-600 italic">TBD</span>
                                    }
                                  </div>
                                  {(activity.consulted_emails || []).length > 0 &&
                                  <div>
                                      <span className="text-xs text-cyan-400 font-semibold">C: </span>
                                      {activity.consulted_emails.map((email, idx) =>
                                    <span key={idx}>
                                          {idx > 0 && ', '}
                                          <OfficerDisplay email={email} />
                                        </span>
                                    )}
                                    </div>
                                  }
                                  {(activity.informed_emails || []).length > 0 &&
                                  <div>
                                      <span className="text-xs text-purple-400 font-semibold">I: </span>
                                      {activity.informed_emails.map((email, idx) =>
                                    <span key={idx}>
                                          {idx > 0 && ', '}
                                          <OfficerDisplay email={email} />
                                        </span>
                                    )}
                                    </div>
                                  }
                                </div>
                              </td>);

                          })}
                        </tr>

                        {/* Key Results Row */}
                        <tr className="border-b border-slate-800">
                          {groupActivities.map((activity) => {
                            if (!expandedOfficers[groupEmail]) return null;
                            const isAccountable = activity.accountable_email === user?.email;
                            return (
                              <td key={activity.id} className="p-4 align-top border-r border-slate-800 last:border-r-0 bg-slate-950/30">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-xs text-slate-500 font-semibold">KEY RESULTS ({(activity.key_results || []).length}) - {activity.key_results_progress || 0}%</div>
                                </div>
                                {(activity.key_results || []).length > 0 ?
                                <div className="space-y-2">
                                    {activity.key_results.map((kr, idx) => {
                                    const current = parseFloat(kr.current_value) || 0;
                                    const target = parseFloat(kr.target) || 1;
                                    const progress = Math.min(current / target * 100, 100);
                                    return (
                                      <div key={idx} className="text-sm">
                                          <div className="text-slate-300 mb-1">{kr.description}</div>
                                          <div className="flex items-center gap-2">
                                            <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                                              <div
                                              className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500"
                                              style={{ width: `${progress}%` }} />

                                            </div>
                                            <div className="text-xs text-slate-400 whitespace-nowrap">
                                              {kr.current_value}/{kr.target} {kr.unit}
                                            </div>
                                          </div>
                                        </div>);

                                  })}
                                  </div> :

                                <div className="text-sm text-slate-600 italic">No key results</div>
                                }
                              </td>);

                          })}
                        </tr>

                        {/* Daily Tasks Row */}
                        <tr className="border-b border-slate-800">
                          {groupActivities.map((activity) => {
                            if (!expandedOfficers[groupEmail]) return null;
                            const activityTasks = dailyTasks.filter(t => t.activity_id === activity.id).sort((a, b) => new Date(b.date) - new Date(a.date));
                            const pendingAITasks = activityTasks.filter(t => t.generated_by_ai && t.status === 'pending_approval');
                            const isAccountable = activity.accountable_email === user?.email;
                            const responsibleOfficer = officers.find(o => o.user_email === activity.responsible_email);
                            const isAIResponsible = responsibleOfficer?.custom_role === 'AI Officer';
                            
                            return (
                              <td key={activity.id} className="p-4 align-top border-r border-slate-800 last:border-r-0">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-xs text-slate-500 font-semibold">DAILY TASKS ({activityTasks.filter(t => t.status === 'approved').length})</div>
                                </div>
                                
                                {/* Bot Task Generation Section */}
                                {isAIResponsible && (
                                  <div className="mb-3">
                                    <BotTaskGenerationSection 
                                      activity={activity}
                                      userEmail={user?.email}
                                    />
                                  </div>
                                )}
                                
                                {/* AI Task Approval Section */}
                                {isAIResponsible && pendingAITasks.length > 0 && (
                                  <div className="mb-3">
                                    <AITaskApprovalSection 
                                      activity={activity}
                                      pendingTasks={pendingAITasks}
                                      isAccountable={isAccountable}
                                    />
                                  </div>
                                )}
                                
                                {activityTasks.filter(t => t.status === 'approved').length > 0 ? (
                                  <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                                    {activityTasks.filter(t => t.status === 'approved').map(task => (
                                      <div 
                                        key={task.id} 
                                        onClick={() => navigate(createPageUrl('dailytasks') + `?date=${task.date}&user=${task.user_email}&taskId=${task.id}`)}
                                        className="text-sm p-2 rounded bg-slate-900/50 border border-slate-800/50 flex flex-col gap-1 cursor-pointer hover:bg-slate-800 transition-colors group"
                                      >
                                        <div className="flex justify-between items-start gap-2">
                                          <span className="text-slate-300 font-medium leading-tight group-hover:text-indigo-400 transition-colors">{task.task_description}</span>
                                          <Badge variant="outline" className={`text-[10px] px-1 py-0 h-5 border whitespace-nowrap ${
                                            task.status_flag === 'green' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                                            task.status_flag === 'yellow' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                                            task.status_flag === 'red' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                                            'bg-slate-500/10 text-slate-400 border-slate-500/30'
                                          }`}>
                                            {task.status_flag?.toUpperCase() || 'N/A'}
                                          </Badge>
                                        </div>
                                        <div className="flex justify-between items-center text-xs text-slate-500 mt-1">
                                          <span className="flex items-center gap-1">
                                            {format(new Date(task.date), 'MMM d')} • <OfficerDisplay email={task.user_email} />
                                          </span>
                                          <span className={task.completed ? "text-emerald-400 flex items-center gap-1 font-medium" : "text-slate-500"}>
                                            {task.completed ? <><CheckCircle className="w-3 h-3" /> Done</> : "Pending"}
                                          </span>
                                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                            <ExternalLink className="w-3 h-3 text-slate-500" />
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-sm text-slate-600 italic">No daily tasks linked</div>
                                )}
                              </td>
                            );
                          })}
                        </tr>

                        {/* Progress Reports Row */}
                        <tr className="border-b border-slate-800">
                          {groupActivities.map((activity) => {
                            if (!expandedOfficers[groupEmail]) return null;
                            const activityReports = progressReports.filter(r => r.activity_id === activity.id).sort((a, b) => new Date(a.reported_at) - new Date(b.reported_at));
                            const pendingReports = activityReports.filter((r) => !r.approved);
                            const isAccountable = activity.accountable_email === user?.email;
                            const isResponsible = activity.responsible_email === user?.email;
                            const canRemoveTags = isAccountable || isResponsible;
                            const { tags: allTags, strugglingFlags } = getAllTagsForActivity(activity);
                            // Default to expanded (true) unless explicitly set to false
                            const isReportsExpanded = expandedReportsInGantt[activity.id] !== false;

                            return (
                              <td key={activity.id} className="p-4 align-top border-r border-slate-800 last:border-r-0">
                                <div className="text-xs text-slate-500 font-semibold mb-2">
                                  PROGRESS REPORTS ({activityReports.length})
                                  {pendingReports.length > 0 && isAccountable &&
                                  <Badge className="ml-2 bg-amber-500/20 text-amber-400 border-none text-xs">
                                      {pendingReports.length} pending
                                    </Badge>
                                  }
                                </div>

                                {activityReports.length > 0 ?
                                <div className="space-y-2">
                                    {/* Click to expand reports */}
                                    <button
                                    onClick={() => setExpandedReportsInGantt((prev) => ({ 
                                      ...prev, 
                                      // Toggle logic: if false -> true, if true/undefined -> false
                                      [activity.id]: prev[activity.id] === false ? true : false 
                                    }))}
                                    className="text-xs text-indigo-400 hover:text-indigo-300 underline">

                                      {isReportsExpanded ? 'Hide reports' : 'Click here to see reports'}
                                    </button>

                                    {/* Expanded reports */}
                                    {isReportsExpanded &&
                                  <AutoScrollContainer className="space-y-2 max-h-60 overflow-y-auto p-2 bg-slate-900/50 rounded">
                                        {activityReports.map((report) => {
                                          return (
                                    <div key={report.id} className={`p-2 rounded text-xs border ${report.approved ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                                            <div className="flex justify-between items-start mb-1">
                                              <div className="text-slate-300 font-semibold">
                                                Reported by {report.reported_by} on {format(new Date(report.reported_at), 'MMM d, yyyy')} at {format(new Date(report.reported_at), 'HH:mm')}
                                              </div>
                                              {/* Proposed status flag badge */}
                                              {report.proposed_status_flag && (
                                                <Badge className={`text-[9px] ${
                                                  report.proposed_status_flag === 'green' ? 'bg-emerald-500/20 text-emerald-400' :
                                                  report.proposed_status_flag === 'red' ? 'bg-red-500/20 text-red-400' :
                                                  'bg-amber-500/20 text-amber-400'
                                                }`}>
                                                  Proposed: {report.proposed_status_flag}
                                                </Badge>
                                              )}
                                            </div>

                                            {/* Key Results Progress */}
                                            {report.key_results_progress && report.key_results_progress.length > 0 &&
                                      <div className="mb-2">
                                                <div className="text-slate-400 font-semibold mb-1">Key Results:</div>
                                                {report.key_results_progress.map((kr, krIdx) =>
                                        <div key={krIdx} className="text-slate-300 ml-2">• {kr.description}: {kr.new_value}</div>
                                        )}
                                              </div>
                                      }

                                            {/* Issues Encountered */}
                                            {(() => {
                                              const reportIssuesList = reportIssues.filter(i => i.progress_report_id === report.id);
                                              if (reportIssuesList.length > 0) {
                                                return (
                                                  <div className="mb-2">
                                                    <div className="text-slate-400 font-semibold mb-1">Issues:</div>
                                                    {reportIssuesList.map((issue, issIdx) =>
                                                      <div key={issIdx} className="text-slate-300 ml-2">• {issue.issue_description}</div>
                                                    )}
                                                  </div>
                                                );
                                              }
                                              return null;
                                            })()}

                                            {/* Tags from progress report */}
                                            {report.tags && (Object.values(report.tags).some(arr => arr && arr.length > 0)) && (
                                              <div className="flex flex-wrap gap-1 mb-2">
                                                {(report.tags.tagged_officers || []).map((email, i) => {
                                                  const officer = officers.find(o => o.user_email === email);
                                                  return (
                                                    <Badge key={`off-${i}`} variant="outline" className="text-[9px] bg-indigo-500/10 text-indigo-400 border-indigo-500/30">
                                                      {officer?.callsign || email}
                                                    </Badge>
                                                  );
                                                })}
                                                {(report.tags.tagged_artifacts || []).map((name, i) => (
                                                  <Badge key={`art-${i}`} variant="outline" className="text-[9px] bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                                                    {name}
                                                  </Badge>
                                                ))}
                                                {(report.tags.tagged_rituals || []).map((name, i) => (
                                                  <Badge key={`rit-${i}`} variant="outline" className="text-[9px] bg-purple-500/10 text-purple-400 border-purple-500/30">
                                                    {name}
                                                  </Badge>
                                                ))}
                                                {(report.tags.tagged_policies || []).map((name, i) => (
                                                  <Badge key={`pol-${i}`} variant="outline" className="text-[9px] bg-amber-500/10 text-amber-400 border-amber-500/30">
                                                    {name}
                                                  </Badge>
                                                ))}
                                                {(report.tags.tagged_capabilities || []).map((name, i) => (
                                                  <Badge key={`cap-${i}`} variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                                    {name}
                                                  </Badge>
                                                ))}
                                              </div>
                                            )}

                                            {/* Additional context */}
                                            {report.additional_context &&
                                      <div className="text-slate-300 text-xs mt-2">{report.additional_context}</div>
                                      }

                                            {/* Approve button - shows proposed flag and applies it on approval */}
                                            {!report.approved && isAccountable &&
                                      <div className="flex items-center gap-2 mt-2">
                                        <Button
                                          size="sm"
                                          className="h-6 text-xs bg-emerald-600 hover:bg-emerald-700"
                                          onClick={() => approveReportMutation.mutate({ activityId: activity.id, reportId: report.id })}>
                                            <ThumbsUp className="w-3 h-3 mr-1" />
                                            Approve {report.proposed_status_flag ? `(Set to ${report.proposed_status_flag})` : ''}
                                        </Button>
                                      </div>
                                      }

                                            {/* Principles validation status */}
                                            {report.principles_validation?.status &&
                                      <Badge className="mt-2 text-xs bg-indigo-500/20 text-indigo-400 border-indigo-500/30">
                                            Principles: {report.principles_validation.status}
                                            </Badge>
                                      }
                                            </div>
                                          );
                                        })}
                                            </AutoScrollContainer>
                                  }

                                    {/* All tags from all reports */}
                                    {(() => {
                                      const hasRemovedFlags = canRemoveTags && activityReports.some(r => (r.removed_flags || []).length > 0);
                                      const hasRemovedTags = canRemoveTags && reportIssues.filter(i => {
                                        const report = progressReports.find(r => r.id === i.progress_report_id);
                                        return report && report.activity_id === activity.id && (i.removed_tags || []).length > 0;
                                      }).length > 0;
                                      
                                      if (strugglingFlags.length === 0 && allTags.length === 0 && !hasRemovedFlags && !hasRemovedTags) {
                                        return null;
                                      }
                                      
                                      return (
                                    <div className="mt-3 p-2 bg-slate-900/30 rounded">
                                        {(strugglingFlags.length > 0 || allTags.length > 0) && (
                                          <>
                                            <div className="text-xs text-slate-500 font-semibold mb-1">All Tags:</div>
                                            <div className="flex flex-wrap gap-1">
                                          {/* Struggling flags first */}
                                          {strugglingFlags.map((flag, idx) => {
                                            const truncatedFlag = flag.flagValue.length > 40 ? flag.flagValue.substring(0, 40) + '...' : flag.flagValue;
                                            return (
                                              <Badge
                                                key={`flag-${idx}`}
                                                className="bg-amber-500/20 text-amber-300 border-amber-500/50 text-xs"
                                                title={flag.flagValue}>
                                                {truncatedFlag} (Struggling)
                                                {canRemoveTags &&
                                                  <button
                                                    type="button"
                                                    onClick={() => removeFlag.mutate({
                                                      reportId: flag.reportId,
                                                      flagValue: flag.flagValue
                                                    })}
                                                    className="ml-1 hover:text-red-400">
                                                    <X className="w-3 h-3" />
                                                  </button>
                                                }
                                              </Badge>
                                            );
                                          })}

                                          {/* Issue tags */}
                                          {allTags.map((tag, idx) => {
                                          const tagLabel = tag.tagType === 'tagged_officers' ?
                                          officers.find((o) => o.user_email === tag.tagValue)?.callsign || tag.tagValue :
                                          tag.tagValue;
                                          const truncatedLabel = tagLabel.length > 40 ? tagLabel.substring(0, 40) + '...' : tagLabel;
                                          const tagTypeLabel = tag.tagType.replace('tagged_', '').replace('_', ' ');

                                          return (
                                          <Badge
                                            key={idx}
                                            variant="outline"
                                            className={`text-xs cursor-pointer ${
                                            tag.tagType === 'tagged_officers' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30 hover:bg-indigo-500/20' :
                                            tag.tagType === 'tagged_artifacts' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/20' :
                                            tag.tagType === 'tagged_rituals' ? 'bg-purple-500/10 text-purple-400 border-purple-500/30 hover:bg-purple-500/20' :
                                            tag.tagType === 'tagged_policies' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20' :
                                            'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'}`
                                            }
                                            title={tagLabel}
                                            onClick={() => {
                                              setTagHistoryDialog({
                                                open: true,
                                                tag: tag
                                              });
                                            }}>

                                                {truncatedLabel} ({tagTypeLabel})
                                                {canRemoveTags &&
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const tagTypeLabel = tag.tagType.replace('tagged_', '').replace('_', ' ');
                                                setTagRemovalDialog({
                                                  open: true,
                                                  tagType: tag.tagType,
                                                  tagValue: tag.tagValue,
                                                  tagLabel: `${tagLabel} (${tagTypeLabel})`
                                                });
                                              }}
                                              className="ml-1 hover:text-red-400">

                                                    <X className="w-3 h-3" />
                                                  </button>
                                            }
                                              </Badge>);

                                          })}
                                            </div>
                                          </>
                                        )}

                                        {/* Show removed flags with reinstate option */}
                                        {canRemoveTags && activityReports.some(r => (r.removed_flags || []).length > 0) &&
                                          <div className="mt-2 space-y-1 border-t border-slate-800 pt-2">
                                            <div className="text-xs text-slate-500 font-semibold mb-1">Removed Flags:</div>
                                            {activityReports.map(report => 
                                              (report.removed_flags || []).map((rf, rfIdx) =>
                                                <div key={`${report.id}-${rfIdx}`} className="text-xs text-slate-500 italic flex items-center gap-2">
                                                  <span>
                                                    {rf.flag_value.length > 40 ? rf.flag_value.substring(0, 40) + '...' : rf.flag_value} - removed by {rf.removed_by} on {format(new Date(rf.removed_at), 'MMM d')} at {format(new Date(rf.removed_at), 'HH:mm')}
                                                  </span>
                                                  <button
                                                    onClick={() => reinstateFlag.mutate({ reportId: report.id, flagValue: rf.flag_value })}
                                                    className="text-emerald-400 hover:text-emerald-300 text-xs underline">
                                                    reinstate
                                                  </button>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        }

                                        {/* Show removed tags with reinstate option */}
                                        {canRemoveTags && reportIssues.filter(i => {
                                          const report = progressReports.find(r => r.id === i.progress_report_id);
                                          return report && report.activity_id === activity.id && (i.removed_tags || []).length > 0;
                                        }).length > 0 &&
                                          <div className="mt-2 space-y-1 border-t border-slate-800 pt-2">
                                            <div className="text-xs text-slate-500 font-semibold mb-1">Removed Tags:</div>
                                            {reportIssues.filter(i => {
                                              const report = progressReports.find(r => r.id === i.progress_report_id);
                                              return report && report.activity_id === activity.id;
                                            }).map(issue => {
                                              const report = progressReports.find(r => r.id === issue.progress_report_id);
                                              const reporter = officers.find(o => o.user_email === report?.reported_by);
                                              return (issue.removed_tags || []).map((rt, rtIdx) => {
                                                const tagValue = rt.tag_type === 'tagged_officers' ?
                                                  officers.find((o) => o.user_email === rt.tag_value)?.callsign || rt.tag_value :
                                                  rt.tag_value;
                                                const truncatedValue = tagValue.length > 40 ? tagValue.substring(0, 40) + '...' : tagValue;
                                                return (
                                                  <div key={`${issue.id}-${rtIdx}`} className="text-xs text-slate-500 italic flex items-center gap-2">
                                                    <span>
                                                      {rt.tag_type.replace('tagged_', '').replace('_', ' ')}: {truncatedValue} - originally added by {reporter?.callsign || report?.reported_by} on {format(new Date(report?.reported_at), 'MMM d')} at {format(new Date(report?.reported_at), 'HH:mm')}, removed by {rt.removed_by} on {format(new Date(rt.removed_at), 'MMM d')} at {format(new Date(rt.removed_at), 'HH:mm')}
                                                    </span>
                                                    <button
                                                      onClick={() => reinstateTag.mutate({
                                                        issueId: issue.id,
                                                        tagType: rt.tag_type,
                                                        tagValue: rt.tag_value
                                                      })}
                                                      className="text-emerald-400 hover:text-emerald-300 text-xs underline">
                                                      reinstate
                                                    </button>
                                                  </div>
                                                );
                                              });
                                            })}
                                          </div>
                                        }
                                      </div>
                                      );
                                    })()}
                                  </div> :

                                <div className="text-sm text-slate-600 italic">No reports yet</div>
                                }
                              </td>);

                          })}
                        </tr>

                        {/* Removed Issues Row */}
                        <tr className="border-b border-slate-800">
                          {groupActivities.map((activity) => {
                            if (!expandedOfficers[groupEmail]) return null;
                            return (
                              <td key={activity.id} className="p-4 align-top border-r border-slate-800 last:border-r-0">
                                <RemovedIssuesSection
                                  activity={activity}
                                  reportIssues={reportIssues}
                                  progressReports={progressReports}
                                  officers={officers}
                                />
                              </td>
                            );
                          })}
                        </tr>

                        {/* Due Date & Actions Row */}
                        <tr>
                          {groupActivities.map((activity) => {
                            if (!expandedOfficers[groupEmail]) return null;
                            const isAccountable = activity.accountable_email === user?.email;
                            const isResponsible = activity.responsible_email === user?.email;
                            return (
                              <td key={activity.id} className="p-4 align-top border-r border-slate-800 last:border-r-0 bg-slate-950/30">
                                <div className="space-y-2">
                                  {activity.due_date &&
                                  <div className="flex items-center gap-2 text-xs text-slate-400">
                                      <Clock className="w-3 h-3" />
                                      Due: {format(new Date(activity.due_date), 'MMM d, yyyy')}
                                    </div>
                                  }
                                  {isResponsible &&
                                  <Button
                                    size="sm"
                                    className="w-full bg-indigo-600 hover:bg-indigo-700 h-8 text-xs"
                                    onClick={() => openReportDialog(activity)}>

                                     <Send className="w-3 h-3 mr-1" />
                                     Submit Report
                                   </Button>
                                  }

                                  {/* Activity-level principles validation */}
                                  {activity.commander_principles_validation?.status &&
                                  <Badge className="text-xs bg-indigo-500/20 text-indigo-400 border-indigo-500/30 w-full justify-center mt-2">
                                     Principles: {activity.commander_principles_validation.status}
                                   </Badge>
                                  }
                                  {isAccountable && (activity.principles_adhered?.length > 0 || activity.red_line_considerations?.length > 0 || activity.narrative_on_principles) &&
                                  <Button
                                    size="sm"
                                    variant="outline" className="bg-green-600 mt-2 px-3 text-xs font-medium rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border shadow-sm hover:text-accent-foreground w-full h-8 border-indigo-500/30 hover:bg-indigo-500/10"

                                    onClick={() => setValidatingActivity(activity)}>

                                     <Shield className="w-3 h-3 mr-1" />
                                     Validate Activity Principles
                                   </Button>
                                  }
                                  </div>
                                  </td>);

                                  })}
                                  </tr>
                                  </tbody>
                                  </table>
                    </div>
                  </div>
                </DragDropContext>
              </div>);

        })
        }
          </div>
      }

      {/* Validation Dialogs */}
      <ActivityPrinciplesValidation
        activity={validatingActivity}
        open={!!validatingActivity}
        onClose={() => setValidatingActivity(null)}
        currentUser={user}
        officers={officers}
        progressReports={progressReports} />


      <ProgressReportPrinciplesValidation
        activity={validatingReport.activity}
        report={validatingReport.report}
        open={!!validatingReport.activity}
        onClose={() => setValidatingReport({ activity: null, report: null })}
        currentUser={user}
        officers={officers} />


      <ArchiveWarningDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveConfirm}
        entityType="Activity"
        entityName={activityToArchive?.title}
        dependents={archiveDependents}
      />

      <TagRemovalDialog
        open={tagRemovalDialog.open}
        onClose={() => setTagRemovalDialog({ open: false, tagType: null, tagValue: null, tagLabel: null })}
        tagType={tagRemovalDialog.tagType}
        tagValue={tagRemovalDialog.tagValue}
        tagLabel={tagRemovalDialog.tagLabel}
        reportIssues={reportIssues}
        progressReports={progressReports}
        officers={officers}
        onConfirm={(issueIds) => {
          removeTagFromReport.mutate({
            issueIds,
            tagType: tagRemovalDialog.tagType,
            tagValue: tagRemovalDialog.tagValue
          });
        }}
      />

      <TagHistoryDialog
        open={tagHistoryDialog.open}
        onClose={() => setTagHistoryDialog({ open: false, tag: null })}
        tag={tagHistoryDialog.tag}
        reportIssues={reportIssues}
        progressReports={progressReports}
        officers={officers}
        activities={activities}
      />

      {/* Report Dialog */}
      <ConfirmableDialog open={reportDialog} onOpenChange={setReportDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submit Progress Report</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            submitReportMutation.mutate({ 
              activityId: selectedActivity.id, 
              report: {
                ...reportForm,
                proposed_status_flag: reportForm.proposed_status_flag || 'yellow'
              }
            });
          }} className="space-y-6 mt-4">
            
            {/* Proposed Status Flag */}
            <div className="space-y-3">
              <Label className="text-lg font-semibold">Activity Status Flag</Label>
              <p className="text-xs text-slate-400">This will be applied when the accountable officer approves the report.</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setReportForm({ ...reportForm, proposed_status_flag: 'green' })}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    reportForm.proposed_status_flag === 'green' 
                      ? 'bg-emerald-500 border-emerald-400 text-white' 
                      : 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30'
                  }`}
                >
                  <Flag className="w-5 h-5 mx-auto mb-1" />
                  <div className="text-xs font-bold">On Track</div>
                </button>
                <button
                  type="button"
                  onClick={() => setReportForm({ ...reportForm, proposed_status_flag: 'yellow' })}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    reportForm.proposed_status_flag === 'yellow' 
                      ? 'bg-amber-500 border-amber-400 text-white' 
                      : 'bg-amber-500/20 border-amber-500/30 text-amber-400 hover:bg-amber-500/30'
                  }`}
                >
                  <Flag className="w-5 h-5 mx-auto mb-1" />
                  <div className="text-xs font-bold">At Risk</div>
                </button>
                <button
                  type="button"
                  onClick={() => setReportForm({ ...reportForm, proposed_status_flag: 'red' })}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                    reportForm.proposed_status_flag === 'red' 
                      ? 'bg-red-500 border-red-400 text-white' 
                      : 'bg-red-500/20 border-red-500/30 text-red-400 hover:bg-red-500/30'
                  }`}
                >
                  <Flag className="w-5 h-5 mx-auto mb-1" />
                  <div className="text-xs font-bold">Off Track</div>
                </button>
              </div>
            </div>

            {/* Key Results Progress */}
            {(reportForm.key_results_progress || []).length > 0 &&
            <div className="space-y-3">
                <Label className="text-lg font-semibold">1. Key Results Progress</Label>
                <p className="text-xs text-slate-400">Update the current value for each key result</p>
                {reportForm.key_results_progress.map((kr, idx) =>
              <div key={idx} className="p-3 bg-slate-950 rounded-lg border border-slate-800">
                    <Label className="text-sm text-slate-300 mb-2 block">{kr.description}</Label>
                    <Input
                  type="number"
                  value={kr.new_value}
                  onChange={(e) => {
                    const updated = [...reportForm.key_results_progress];
                    updated[idx] = { ...updated[idx], new_value: e.target.value };
                    setReportForm({ ...reportForm, key_results_progress: updated });
                  }}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                  placeholder="Enter new value..."
                  required />

                  </div>
              )}
              </div>
            }

            {/* Issues Encountered */}
            <div className="space-y-3">
              <div>
                <Label className="text-lg font-semibold">2. Issues Encountered</Label>
                <p className="text-xs text-slate-400">Document any blockers or challenges</p>
              </div>
              <Button type="button" onClick={addIssue} className="w-full bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Issue
              </Button>
              
              {reportForm.issues_encountered.map((issue, issueIdx) =>
              <div key={issueIdx} className="p-4 bg-slate-950 rounded-lg border border-slate-800 space-y-3">
                  <div className="flex justify-between items-start">
                    <Textarea
                    placeholder="Describe the issue..."
                    value={issue.issue_description}
                    onChange={(e) => updateIssue(issueIdx, 'issue_description', e.target.value)}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 flex-1"
                    rows={2} />

                    <Button type="button" size="icon" variant="ghost" onClick={() => removeIssue(issueIdx)} className="ml-2">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>

                  {/* Tag Elements */}
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-400">Tag Elements</Label>

                    {/* Display tagged elements */}
                    <div className="flex flex-wrap gap-1 min-h-[28px] p-2 bg-slate-900 rounded border border-slate-700">
                      {(issue.tagged_officers || []).map((officer, i) =>
                    <Badge key={i} variant="secondary" className="bg-indigo-500/10 text-indigo-400">
                         {officers.find((o) => o.user_email === officer)?.callsign || officer} (Officer)
                         <button
                        type="button"
                        onClick={() => removeTagFromIssue(issueIdx, 'tagged_officers', officer)}
                        className="ml-1 hover:text-red-400">

                           <X className="w-3 h-3" />
                         </button>
                       </Badge>
                    )}
                      {(issue.tagged_artifacts || []).map((tag, i) =>
                    <Badge key={i} variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                         {tag} (Artifact)
                         <button
                        type="button"
                        onClick={() => removeTagFromIssue(issueIdx, 'tagged_artifacts', tag)}
                        className="ml-1 hover:text-red-400">

                           <X className="w-3 h-3" />
                         </button>
                       </Badge>
                    )}
                      {(issue.tagged_rituals || []).map((tag, i) =>
                    <Badge key={i} variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">
                         {tag} (Ritual)
                         <button
                        type="button"
                        onClick={() => removeTagFromIssue(issueIdx, 'tagged_rituals', tag)}
                        className="ml-1 hover:text-red-400">

                           <X className="w-3 h-3" />
                         </button>
                       </Badge>
                    )}
                      {(issue.tagged_policies || []).map((tag, i) =>
                    <Badge key={i} variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                         {tag} (Policy)
                         <button
                        type="button"
                        onClick={() => removeTagFromIssue(issueIdx, 'tagged_policies', tag)}
                        className="ml-1 hover:text-red-400">

                           <X className="w-3 h-3" />
                         </button>
                       </Badge>
                    )}
                      {(issue.tagged_capabilities || []).map((tag, i) =>
                    <Badge key={i} variant="outline" className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                         {tag} (Capability)
                         <button
                        type="button"
                        onClick={() => removeTagFromIssue(issueIdx, 'tagged_capabilities', tag)}
                        className="ml-1 hover:text-red-400">

                           <X className="w-3 h-3" />
                         </button>
                       </Badge>
                    )}
                    </div>

                    {/* Tag selection */}
                    <div className="flex gap-2">
                      <Select
                      value={currentTagType}
                      onValueChange={(type) => {
                        setCurrentTagType(type);
                        setCurrentTagValue('');
                      }}>

                        <SelectTrigger className="bg-slate-900 border-slate-700 h-9 text-sm w-40">
                          <SelectValue placeholder="Add tag..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Officer">Officer</SelectItem>
                          <SelectItem value="Artifact">Artifact</SelectItem>
                          <SelectItem value="Ritual">Ritual</SelectItem>
                          <SelectItem value="Policy">Policy</SelectItem>
                          <SelectItem value="Capability">Capability</SelectItem>
                        </SelectContent>
                      </Select>

                      {currentTagType === 'Officer' ?
                    <SearchableTagSelect
                      options={officers
                        .filter((o) => !issue.tagged_officers?.includes(o.user_email) && o.user_email)
                        .map((o) => ({ value: o.user_email, label: o.callsign }))}
                      value=""
                      onValueChange={(value) => {
                        addTagToIssue(issueIdx, 'Officer', value);
                        setCurrentTagType('');
                      }}
                      placeholder="Select officer..."
                      emptyMessage="No officers found."
                      className="flex-1 h-9 text-sm"
                    /> :
                    currentTagType === 'Artifact' ?
                    <SearchableTagSelect
                      options={artifacts
                        .filter((a) => {
                          if (!a.name || issue.tagged_artifacts?.includes(a.name)) return false;
                          // Filter company secrets
                          if (a.is_company_secret) {
                            if (a.owner_email === user?.email) return true;
                            if ((a.editors || []).includes(user?.email)) return true;
                            if ((a.viewers || []).includes(user?.email)) return true;
                            return false;
                          }
                          return true;
                        })
                        .map((a) => ({ value: a.name, label: a.name }))}
                      value=""
                      onValueChange={(value) => {
                        addTagToIssue(issueIdx, 'Artifact', value);
                        setCurrentTagType('');
                      }}
                      placeholder="Select artifact..."
                      emptyMessage="No artifacts found."
                      className="flex-1 h-9 text-sm"
                    /> :
                    currentTagType === 'Ritual' ?
                    <SearchableTagSelect
                      options={rituals
                        .filter((r) => {
                          if (!r.name || issue.tagged_rituals?.includes(r.name)) return false;
                          // Filter company secrets
                          if (r.is_company_secret) {
                            if (r.owner_email === user?.email) return true;
                            if ((r.participants || []).includes(user?.email)) return true;
                            return false;
                          }
                          return true;
                        })
                        .map((r) => ({ value: r.name, label: r.name }))}
                      value=""
                      onValueChange={(value) => {
                        addTagToIssue(issueIdx, 'Ritual', value);
                        setCurrentTagType('');
                      }}
                      placeholder="Select ritual..."
                      emptyMessage="No rituals found."
                      className="flex-1 h-9 text-sm"
                    /> :
                    currentTagType === 'Policy' ?
                    <SearchableTagSelect
                      options={policies
                        .filter((p) => !issue.tagged_policies?.includes(p.title) && p.title)
                        .map((p) => ({ value: p.title, label: p.title }))}
                      value=""
                      onValueChange={(value) => {
                        addTagToIssue(issueIdx, 'Policy', value);
                        setCurrentTagType('');
                      }}
                      placeholder="Select policy..."
                      emptyMessage="No policies found."
                      className="flex-1 h-9 text-sm"
                    /> :
                    currentTagType === 'Capability' ?
                    <SearchableTagSelect
                      options={capabilities
                        .filter((c) => !issue.tagged_capabilities?.includes(c.name) && c.name)
                        .map((c) => ({ value: c.name, label: c.name }))}
                      value=""
                      onValueChange={(value) => {
                        addTagToIssue(issueIdx, 'Capability', value);
                        setCurrentTagType('');
                      }}
                      placeholder="Select capability..."
                      emptyMessage="No capabilities found."
                      className="flex-1 h-9 text-sm"
                    /> :
                    null}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Principles & Red Lines */}
            <PrinciplesSection
              mode="flag"
              principlesAdhered={selectedActivity?.principles_adhered || []}
              redLineConsiderations={selectedActivity?.red_line_considerations || []}
              flaggedItems={reportForm.flagged_items}
              narrative={reportForm.narrative_on_principles}
              onPrinciplesChange={(flags) => setReportForm({ ...reportForm, flagged_items: flags })}
              onNarrativeChange={(narrative) => setReportForm({ ...reportForm, narrative_on_principles: narrative })} />


            {/* Additional Context */}
            <div className="space-y-2">
              <Label className="text-lg font-semibold">3. Additional Context</Label>
              <p className="text-xs text-slate-400">Any other information or notes</p>
              <Textarea
                value={reportForm.additional_context}
                onChange={(e) => setReportForm({ ...reportForm, additional_context: e.target.value })}
                className="bg-slate-950 border-slate-800 min-h-[100px]"
                placeholder="Add any additional context or notes..." />

            </div>

            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 sticky bottom-0">
              <Send className="w-4 h-4 mr-2" />
              Submit Report
            </Button>
          </form>
        </DialogContent>
      </ConfirmableDialog>

      <ArchiveWarningDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveConfirm}
        entityType="Activity"
        entityName={activityToArchive?.title}
        dependents={archiveDependents}
      />
    </div>
  );
}